import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useContext } from "react";
import _ from "lodash";
import { DateTime } from "luxon";
import { MemberContext } from "../MemberZone";
import { localImageSrc } from "app/images";
import { analyticsTrack } from "app/analytics/track";
import { videoAnalyticsEvents } from "features/landingPage/FunnelMVP/videoHelpers";
import { useLogEventOnLoad } from "hooks/useLogEventOnLoad";
import { formatTimestamp } from "@app/shared/utils";
import { CancellationFlow } from "./CancellationFlow";
import { CancellationDateType } from "./CancellationFlowPage7";

const useStyles = makeStyles((theme) => ({
    completeCancellationButton: {
        width: "max-content",
        marginLeft: "auto",
    },

    videoContainer: {
        overflow: "hidden",

        marginBottom: theme.spacing(2),

        display: "flex",
        flexDirection: "column",
    },
    video: {
        width: "100%",
        maxWidth: 1200,
        height: "auto",
        maxHeight: "100%",
        display: "block",
        margin: "auto",
        borderRadius: theme.borderRadius.default,
        "&::cue": {
            fontSize: "70%",
            [theme.breakpoints.down("md")]: {
                fontSize: "100%",
            },
        },
    },
    renewalDate: {
        display: "inline-block",
        maxWidth: 500,
        padding: theme.spacing(2),
    },
}));

export const CancellationFlowPage8 = (props: {
    cancellationDateType: CancellationDateType;
}): JSX.Element => {
    const classes = useStyles();

    const memberContext = useContext(MemberContext);
    const activeMemberships = memberContext.memberships;
    const membership = _.first(activeMemberships);
    const renewalDate = formatTimestamp(
        DateTime.fromISO(membership?.renewalDate ?? "").toMillis(),
        {
            month: "long",
            day: "numeric",
            year: "numeric",
        },
    );
    const pageId = "farewellMeditation";
    useLogEventOnLoad(`cancellationFlow.${pageId}.viewed`);

    return (
        <CancellationFlow
            pageId={pageId}
            content={
                <>
                    <>
                        <Typography variant="body1">
                            Last thing before your cancellation.
                        </Typography>
                        <Typography variant="h2">A farewell meditation for you.</Typography>
                    </>
                    <>
                        {
                            <div className={classes.videoContainer}>
                                {/*eslint-disable-next-line jsx-a11y/media-has-caption*/}
                                <video
                                    controls
                                    className={classes.video}
                                    data-testid={"video.farewellMeditation"}
                                    poster={localImageSrc(
                                        "cancellation/farewellMeditationThumbnail.jpg",
                                    )}
                                    {...videoAnalyticsEvents(pageId)}
                                    onEnded={() => {
                                        analyticsTrack(`video.${pageId}.ended`);
                                    }}
                                >
                                    <source
                                        src="https://storage.googleapis.com/cloud-sangha-images/farewell-meditation-video/farewell_meditation.mp4"
                                        type="video/mp4"
                                    />
                                    Sorry, your browser doesn't support embedded videos.
                                </video>
                            </div>
                        }
                    </>
                    <>
                        {props.cancellationDateType ===
                            CancellationDateType.END_OF_BILLING_CYCLE && (
                            <Typography variant="caption" className={classes.renewalDate}>
                                After cancelling, you'll still have full access until the end of
                                your current billing cycle on <strong>{`${renewalDate}`}</strong>
                            </Typography>
                        )}
                    </>
                </>
            }
        />
    );
};
